import {
  About_us_Img,
  About_us_Img_2,
  AI_BG_NEW,
  Clarity_Icon,
  Data_ETL_Icon,
  Data_layer_Icon,
  Data_Speed_Icon,
  Data_Syntax_Icon,
  Our_Product_Bg,
} from "../../../assets";
import { Helmet, HelmetData } from "react-helmet-async";
import { useModal } from "../../../utils/demoProvider";
import { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import {
  MOTION_OK,
  revealTrigger,
  riseFrom,
  maskedLineReveal,
} from "../../../utils/animations";
const helmetData = new HelmetData({});

const why = [
  {
    icon: Clarity_Icon,
    title: "Analytics Engine",
    text: "Proprietary analytics processing for querying, transforming, and analysing datasets at petabyte scale, the processing core of the Analytics umbrella.",
    discipline: "Analytics",
  },
  {
    icon: Data_Syntax_Icon,
    title: "DataSyntax",
    text: "A query language combining natural language and command syntax - enabling both business users and technical analysts to interrogate data precisely, in the way that comes most naturaly to them.",
    discipline: "Analytics",
  },
  {
    icon: Data_layer_Icon,
    title: "AI DataLayer",
    text: "Semantic infrastructure that standardises and enriches interaction data into formats optimised for machine learning - making raw event data ready for AI reasoning without additional transformation overhead.",
    discipline: "Analytics",
  },
  {
    icon: Data_ETL_Icon,
    title: "Data ETL",
    text: "Pipeline infrastructure for batch and streaming data from any source - the connective tissue that brings external data into the [Al] stack without bespoke integration work.",
    discipline: "Intelligence",
  },
  {
    icon: Data_ETL_Icon,
    title: "CORE Model Family",
    text: "Four fine-tuned analytics reasoning models - Lite, Heavy, and Alpha - each purpose-built for a diferent depth of analytical and financial reasoning, allwith chain-of-thought transparency.",
    discipline: "Analytics",
  },
  {
    icon: Data_Speed_Icon,
    title: "Speedata APU Integration",
    text: "Through our strategic partnership with Speedata, [AI] Platform delivers Apache Spark on Analytics Processing Units - purpose-built silicon that processes big data workloads at 100x the speed of commodity cloud compute",
    discipline: "Analytics",
  },
];

export default function AboutUS() {
  const { openModal } = useModal();
  const pageRef = useRef(null);

  useGSAP(
    () => {
      const mm = gsap.matchMedia();
      mm.add(MOTION_OK, () => {
        riseFrom(".au-hero-item", {
          delay: 0.2,
          stagger: 0.12,
          scrollTrigger: revealTrigger(".au-hero-item", "top 85%"),
        });
        riseFrom(".au-hero-media", {
          y: 40,
          duration: 1.1,
          delay: 0.3,
          scrollTrigger: revealTrigger(".au-hero-media", "top 85%"),
        });
        riseFrom(".au-beliefs-head > *", {
          stagger: 0.15,
          scrollTrigger: revealTrigger(".au-beliefs-head"),
        });
        riseFrom(".au-belief-card", {
          y: 48,
          duration: 1,
          stagger: 0.12,
          scrollTrigger: revealTrigger(".au-beliefs-grid", "top 75%"),
        });
        riseFrom(".au-beliefs-media", {
          y: 40,
          duration: 1.1,
          scrollTrigger: revealTrigger(".au-beliefs-row", "top 75%"),
        });
        riseFrom(".au-tech-head > *", {
          stagger: 0.15,
          scrollTrigger: revealTrigger(".au-tech-head"),
        });
        riseFrom(".au-tech-card", {
          y: 48,
          duration: 1,
          stagger: 0.12,
          scrollTrigger: revealTrigger(".au-tech-grid", "top 75%"),
        });
        maskedLineReveal(pageRef.current.querySelector(".au-cta-heading"), {
          scrollTrigger: revealTrigger(".au-cta-heading"),
        });
        riseFrom(".au-cta-copy", {
          delay: 0.2,
          scrollTrigger: revealTrigger(".au-cta-copy", "top 85%"),
        });
      });
      return () => mm.revert();
    },
    { scope: pageRef }
  );

  return (
    <>
      <Helmet helmetData={helmetData}>
        <title>
          About Analytics Intelligence | AI &amp; Analytics Infrastructure
        </title>
        <link
          rel="canonical"
          href="https://analyticsintelligence.com/about-us"
        />
        <meta
          name="description"
          content="Analytics Intelligence builds the vertically integrated infrastructure and AI that make data actionable — serving digital analytics teams and quantitative trading desks worldwide."
        />
        <meta
          name="keywords"
          content="Analytics Intelligence, data analytics company, generative AI company, vertically integrated AI stack, Analytics Engine, CORE models, DataSyntax, Speedata APU, digital analytics teams, quantitative trading, enterprise AI infrastructure"
        />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Analytics Intelligence Ltd" />
        <meta
          property="og:title"
          content="About Analytics Intelligence | AI & Analytics Infrastructure"
        />
        <meta
          property="og:description"
          content="A data analytics and generative AI company building the infrastructure that makes data useful and the AI that makes it actionable — for analytics teams and trading desks."
        />
        <meta
          property="og:url"
          content="https://analyticsintelligence.com/about-us"
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:image"
          content="https://analyticsintelligence.com/favicon.png"
        />
        <meta property="og:site_name" content="Analytics Intelligence" />
        <meta property="og:locale" content="en_GB" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:site" content="@_AnalyticsIntel" />
        <meta
          name="twitter:title"
          content="About Analytics Intelligence | AI & Analytics Infrastructure"
        />
        <meta
          name="twitter:description"
          content="A data analytics and generative AI company building the infrastructure that makes data useful and the AI that makes it actionable — for analytics teams and trading desks."
        />
        <meta
          name="twitter:image"
          content="https://analyticsintelligence.com/favicon.png"
        />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "AboutPage",
            "name": "About Analytics Intelligence",
            "description": "Analytics Intelligence is a data analytics and generative AI company building a vertically integrated stack — from Speedata APU compute and the Analytics Engine to the CORE family of reasoning models — that serves digital analytics teams and quantitative trading desks.",
            "url": "https://analyticsintelligence.com/about-us",
            "publisher": {
              "@type": "Organization",
              "name": "Analytics Intelligence Ltd",
              "alternateName": ["Analytics Intelligence", "Analytics Intelligence Mauritius", "[AI]"],
              "url": "https://analyticsintelligence.com",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Port Louis",
                "addressCountry": "MU"
              },
              "areaServed": ["Mauritius", "Africa", "United Kingdom", "Europe", "North America", "South America", "Australia"]
            }
          }
        `}</script>
      </Helmet>
      <main ref={pageRef}>
        <section
          style={{
            backgroundImage: `url("${AI_BG_NEW}")`,
            backgroundPosition: "center",
            backgroundSize: "cover",
          }}
          className="min-h-[100vh] flex items-center"
        >
          <div className="container mx-auto w-full h-full pt-[12rem] pb-[96px]">
            <div className="flex flex-col items-center gap-16">
              <div className="flex flex-col gap-4 items-center">
                <span className="au-hero-item text-sm md:text-base bg-[#EFF0F71F] px-3 p-[6px] rounded-[100px] text-white flex items-center gap-2">
                  <div className="bg-[#0073FF] h-2 w-2 rounded-full"></div>
                  <div className="bg-[#D29505] h-2 w-2 rounded-full"></div>
                  <span>About Us</span>
                </span>
                <div className="flex flex-col items-center gap-8">
                  <h2 className="au-hero-item text-[38px] md:text-[48px] text-center font-extrabold leading-[1.3] md:leading-[54px] text-white">
                    We build the infrastructure for{" "}
                    <br className="hidden md:block" /> data-driven decisions
                  </h2>

                  <div className="flex flex-col-reverse md:flex-row items-center gap-8">
                    <div className="flex-1 flex items-center justify-center">
                      <img
                        src={About_us_Img}
                        alt="Data Structures"
                        className="au-hero-media w-full md:max-w-[400px] h-auto"
                      />
                    </div>

                    <p className="au-hero-item text-sm md:text-base text-center md:text-left text-white flex-1 max-w-[600px]">
                      [AI] Analytics Intelligence is a data analytics and
                      generative AI company focused on two things: the
                      infrastructure that makes data useful, and the AI that
                      makes it actionable. We serve digital analytics teams and
                      quantitative trading desks, two markets where the speed
                      and quality of decisions directly determine outcomes.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        <section className="bg-[#010127] relative pt-[48px] pb-[96px] md:py-[96px]">
          <div className="container relative flex flex-col gap-11">
            <div className="w-[150px] lg:w-[400px] h-[300px] bg-[#0073FF]/50 blur-[150px] absolute top-[30%] left-[50%] translate-x-[-50%] translate-y-[-50%]"></div>

            <div className="au-beliefs-head flex flex-col items-center text-center md:text-left z-[800]">
              <span className="text-sm md:text-base bg-[#EFF0F71F] px-3 p-[6px] rounded-[100px] text-white">
                What We Believe
              </span>
              <h2 className="text-white font-extrabold text-[24px] md:text-[32px] text-center">
                Four convictions that shape every product decision
              </h2>
            </div>
            <div className="au-beliefs-row flex flex-col md:flex-row items-center mt-0 md:mt-11 gap-8">
              <div className="au-beliefs-grid flex-1 flex flex-col gap-y-[24px] max-w-[590px]">
                <div className="au-belief-card bg-[#40406C80] backdrop-blur-[4px] p-4 rounded-xl">
                  <h4 className="text-lg md:text-xl font-extrabold text-white">
                    Infrastructure returns should be measurable
                  </h4>
                  <p className="text-sm md:text-base text-white">
                    Analytics infrastructure is a strategic investment - and
                    like any investment, it should produce quantifable returns.
                    We build infrastructure with benchmarkable performance
                    claims and commission enterprise evaluations before
                    commitments are made. "Faster" is not a product claim. 100x
                    is.
                  </p>
                </div>
                <div className="au-belief-card bg-[#40406C80] backdrop-blur-[4px] p-4 rounded-xl">
                  <h4 className="text-base md:text-lg lg:text-xl font-extrabold text-white">
                    Specialised Al outperforms general-purpose Al for domain
                    tasks
                  </h4>
                  <p className="text-sm md:text-base text-white">
                    General-purpose large language models are impressive
                    generalists. For domain-specific analytical reasoning -
                    decomposing a revenue anomaly. evaluating a quantitative
                    strategy across market regimes, atributing multi-channel
                    performance - they are expensive, imprecise, and slow. We
                    build domain-specialist
                  </p>
                </div>
                <div className="au-belief-card bg-[#40406C80] backdrop-blur-[4px] p-4 rounded-xl">
                  <h4 className="text-base md:text-lg lg:text-xl font-extrabold text-white">
                    Autonomous agents require governance, not just capability.
                  </h4>
                  <p className="text-sm md:text-base text-white">
                    The next generation of enterprise analytics is not a smarter
                    dashboard — it is an autonomous agent that monitors,
                    reasons, and acts. But autonomous capability without
                    enterprise-grade governance is a liability, not a product.
                    We build governance controls, audit logs, and permission
                    scopes into every agent before we build the capabilities.
                  </p>
                </div>
                <div className="au-belief-card bg-[#40406C80] backdrop-blur-[4px] p-4 rounded-xl">
                  <h4 className="text-base md:text-lg lg:text-xl font-extrabold text-white">
                    Vertical integration creates compounding performance returns
                  </h4>
                  <p className="text-sm md:text-base text-white">
                    When your data colection infrastructure, compute layer,
                    query language, Al models, and autonomous agents are al
                    built and optimised by the same team, every layer improves
                    every other layer. The result is faster, cheaper, and more
                    reliable than any combination of best-of-breed third-party
                    tools - and gets better compounding returns as the stack
                    matures.
                  </p>
                </div>
              </div>

              <div className="flex-1 flex items-center justify-center">
                <img
                  src={About_us_Img_2}
                  alt="Data Structures"
                  className="au-beliefs-media md:max-w-[522px] h-auto"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="bg-[#EDF1FF] py-[48px]">
          <div className="container space-y-12">
            <div className="au-tech-head flex flex-col items-center text-center md:text-left z-[800]">
              <span className="text-sm md:text-base bg-[#C7E0FF] px-3 p-[6px] rounded-[100px] text-[#020128">
                Our Technology
              </span>
              <h2 className="text-[#020128] font-extrabold text-[24px] md:text-[32px] text-center">
                The complete verticaly integrated stack
              </h2>
            </div>

            <div className="au-tech-grid grid grid-cols-12 gap-8">
              {why.map((item, idx) => (
                <div
                  key={idx}
                  className="au-tech-card group col-span-12 md:col-span-6 lg:col-span-4 bg-white hover:bg-poppingBlue flex flex-col items-start rounded-xl px-3 py-6 gap-4 transition-colors duration-700"
                >
                  <img
                    src={item.icon}
                    alt={`${item.title} illustration`}
                    className="w-16 h-16 transition-all duration-700 group-hover:brightness-0 group-hover:invert"
                  />
                  <div className="space-y-2 text-left">
                    <div
                      className={`py-[6px] px-3 border ${item.discipline === "Analytics" ? "border-[#186FDE] text-[#186FDE] bg-[#186FDE]/20" : "border-[#D29505] text-[#D29505] bg-[#D29505]/20"} rounded-[100px] w-fit text-sm transition-colors duration-700 group-hover:border-white group-hover:text-white group-hover:bg-white/20`}
                    >
                      {item.discipline}
                    </div>
                    <h5 className="text-lg md:text-xl font-bold text-[#020128] transition-colors duration-700 group-hover:text-white">
                      {item.title}
                    </h5>
                    <p className="text-sm md:text-base text-[#020128] transition-colors duration-700 group-hover:text-white">
                      {item.text}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section
          className="relative pt-[116px]"
          style={{
            backgroundImage: `url("${Our_Product_Bg}")`,
            backgroundPosition: "center",
            backgroundSize: "cover",
            backgroundRepeat: "no-repeat",
          }}
        >
          <div className="container">
            <div className="bg-[#1E1E4080] backdrop-blur-[20px] flex flex-col items-center gap-6 px-4 md:px-8 py-8 sm:py-12 md:py-14 lg:py-16 xl:py-20 rounded-3xl">
              <div className="">
                <h2
                  className="au-cta-heading text-[32px] md:text-[44px] text-center font-extrabold text-white"
                  dangerouslySetInnerHTML={{
                    __html: "Your Enterprise AI Future Starts Here",
                  }}
                />
                <p className="au-cta-copy text-sm md:text-base text-center text-[#FFFFFF] max-w-[680px]">
                  With [AI], enterprises unlock secure data infrastructure,
                  domain-aware models, and AI-powered agents, enabling faster
                  decisions, stronger execution, and new value creation.
                </p>
              </div>

              <button
                onClick={openModal}
                className="text-sm md:text-base font-semibold py-3 px-4 md:px-6 min-w-[170px] text-center btn-sweep btn-sweep-blue text-white rounded-[100px] gap-2"
              >
                Explore Products
              </button>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
